/**
 * Button and bubbly-button animation effects: trigger JavaScript + CSS,
 * selected per state at render time. Scroll-reveal animation lives in
 * shared/scroll-animation-runtime-source.js (registry-driven, M6); the AOS
 * fallback CSS lives in shared/template-support-css.js, gated on the runtime
 * handshake and scoped to the owning template.
 */

import { DEFAULT_BUTTON_SELECTOR } from '../shared/button-vocabulary.js';

// ── Bubbly Button ──────────────────────────────────────────────────────
// Adds .animate class on mouseenter, removes on animationend. Idempotent —
// safe to inject in every render; a no-op when no .bubbly-button elements exist.
export const BUBBLY_BUTTON_JS = `
(function() {
  var btns = document.querySelectorAll('.bubbly-button');
  for (var i = 0; i < btns.length; i++) {
    (function(btn) {
      btn.addEventListener('mouseenter', function() {
        btn.classList.remove('animate');
        void btn.offsetWidth;
        btn.classList.add('animate');
      });
      btn.addEventListener('animationend', function() {
        btn.classList.remove('animate');
      });
    })(btns[i]);
  }
})();`;

// ── Button Effect Cleanup ──────────────────────────────────────────────
// Every JS-based effect starts with this preamble to remove listeners from
// any previously active effect. Each effect stores a cleanup fn on the element.
// The selector every effect is written against. It used to be a module-level
// constant baked into all eleven effect strings at import time, which is what
// made the button controls unreachable on the ~13% of templates that do not
// use the CodeStitch class names: the CSS was emitted, it just never matched.
// Each effect is now a function of the selector, and `buildButtonEffectRegistry`
// memoizes per selector so a render does not rebuild eleven template literals.
// `BUTTON_SEL` remains exported as the default for callers with no project in
// scope, and a project deriving only the baseline pair gets byte-identical
// output. See shared/button-vocabulary.js for how a project's set is derived.
export { DEFAULT_BUTTON_SELECTOR as BUTTON_SEL };
const EFFECT_CLEANUP = (SEL) => `
(function() {
  var btns = document.querySelectorAll('${SEL}');
  for (var i = 0; i < btns.length; i++) {
    if (btns[i]._pbEffectCleanup) { btns[i]._pbEffectCleanup(); btns[i]._pbEffectCleanup = null; }
    btns[i].style.transform = '';
    btns[i].classList.remove('pb-animate');
  }
})();`;

// ── Button Hover Effect: Bubble Burst ─────────────────────────────────

const BUBBLE_EFFECT_CSS = (SEL) => `
${SEL} { overflow: visible; position: relative; }
${SEL}::before {
  content: ""; position: absolute; display: none;
  width: 140%; height: 100%; left: -20%; top: -75%; z-index: 2;
  background-color: transparent !important;
  background-repeat: no-repeat; transition: all ease-in-out 0.5s;
  pointer-events: none;
  background-image: radial-gradient(circle, var(--primary) 20%, transparent 20%), radial-gradient(circle, transparent 20%, var(--primary) 20%, transparent 30%), radial-gradient(circle, var(--primary) 20%, transparent 20%), radial-gradient(circle, var(--primary) 20%, transparent 20%), radial-gradient(circle, transparent 10%, var(--primary) 15%, transparent 20%), radial-gradient(circle, var(--primary) 20%, transparent 20%), radial-gradient(circle, var(--primary) 20%, transparent 20%), radial-gradient(circle, var(--primary) 20%, transparent 20%), radial-gradient(circle, var(--primary) 20%, transparent 20%);
  background-size: 10% 10%, 20% 20%, 15% 15%, 20% 20%, 18% 18%, 10% 10%, 15% 15%, 10% 10%, 18% 18%;
}
${SEL}::after {
  content: ""; position: absolute; display: none;
  width: 140%; height: 100%; left: -20%; bottom: -75%; z-index: 2;
  background-color: transparent !important;
  background-repeat: no-repeat; transition: all ease-in-out 0.5s;
  pointer-events: none;
  background-image: radial-gradient(circle, var(--primary) 20%, transparent 20%), radial-gradient(circle, var(--primary) 20%, transparent 20%), radial-gradient(circle, transparent 10%, var(--primary) 15%, transparent 20%), radial-gradient(circle, var(--primary) 20%, transparent 20%), radial-gradient(circle, var(--primary) 20%, transparent 20%), radial-gradient(circle, var(--primary) 20%, transparent 20%), radial-gradient(circle, var(--primary) 20%, transparent 20%);
  background-size: 15% 15%, 20% 20%, 18% 18%, 20% 20%, 15% 15%, 10% 10%, 20% 20%;
}
${SEL}.pb-animate::before { display: block; animation: pbBubbleTop 0.75s ease-in-out forwards; }
${SEL}.pb-animate::after { display: block; animation: pbBubbleBottom 0.75s ease-in-out forwards; }
@keyframes pbBubbleTop {
  0% { background-position: 5% 90%, 10% 90%, 10% 90%, 15% 90%, 25% 90%, 25% 90%, 40% 90%, 55% 90%, 70% 90%; }
  50% { background-position: 0% 80%, 0% 20%, 10% 40%, 20% 0%, 30% 30%, 22% 50%, 50% 50%, 65% 20%, 90% 30%; }
  100% { background-position: 0% 70%, 0% 10%, 10% 30%, 20% -10%, 30% 20%, 22% 40%, 50% 40%, 65% 10%, 90% 20%; background-size: 0% 0%, 0% 0%, 0% 0%, 0% 0%, 0% 0%, 0% 0%; }
}
@keyframes pbBubbleBottom {
  0% { background-position: 10% -10%, 30% 10%, 55% -10%, 70% -10%, 85% -10%, 70% -10%, 70% 0%; }
  50% { background-position: 0% 80%, 20% 80%, 45% 60%, 60% 100%, 75% 70%, 95% 60%, 105% 0%; }
  100% { background-position: 0% 90%, 20% 90%, 45% 70%, 60% 110%, 75% 80%, 95% 70%, 110% 10%; background-size: 0% 0%, 0% 0%, 0% 0%, 0% 0%, 0% 0%, 0% 0%; }
}`;

const BUBBLE_EFFECT_JS = (SEL) =>
  EFFECT_CLEANUP(SEL) +
  `
(function() {
  var btns = document.querySelectorAll('${SEL}');
  for (var i = 0; i < btns.length; i++) {
    (function(btn) {
      function trigger() {
        btn.classList.remove('pb-animate');
        void btn.offsetWidth;
        btn.classList.add('pb-animate');
      }
      function onEnd() { btn.classList.remove('pb-animate'); }
      btn.addEventListener('mouseenter', trigger);
      btn.addEventListener('focus', trigger);
      btn.addEventListener('animationend', onEnd);
      btn._pbEffectCleanup = function() {
        btn.removeEventListener('mouseenter', trigger);
        btn.removeEventListener('focus', trigger);
        btn.removeEventListener('animationend', onEnd);
      };
    })(btns[i]);
  }
})();`;

// ── Button Hover Effect: Shimmer ──────────────────────────────────────
const SHINE_EFFECT_CSS = (SEL) => `
${SEL} { overflow: hidden; position: relative; isolation: isolate; }
${SEL}::before { display: none !important; }
${SEL}::after {
  content: ""; position: absolute; display: block !important;
  top: -120%; left: -220%; width: 75%; height: 340%;
  background: linear-gradient(120deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.1) 38%, rgba(255,255,255,0.9) 50%, rgba(255,255,255,0.1) 62%, rgba(255,255,255,0) 100%);
  border-radius: inherit; transform: skewX(-22deg); transition: none; opacity: 0;
  z-index: 2; pointer-events: none;
}
${SEL}.pb-animate::after {
  display: block !important;
  animation: pbShimmer 0.9s ease-in-out;
}
@keyframes pbShimmer {
  0% { left: -220%; opacity: 0; }
  20% { opacity: 1; }
  80% { opacity: 1; }
  100% { left: 220%; opacity: 0; }
}`;

const SHINE_EFFECT_JS = (SEL) =>
  EFFECT_CLEANUP(SEL) +
  `
(function() {
  var btns = document.querySelectorAll('${SEL}');
  for (var i = 0; i < btns.length; i++) {
    (function(btn) {
      function trigger() {
        btn.classList.remove('pb-animate');
        void btn.offsetWidth;
        btn.classList.add('pb-animate');
      }
      function onEnd() { btn.classList.remove('pb-animate'); }
      btn.addEventListener('mouseenter', trigger);
      btn.addEventListener('focus', trigger);
      btn.addEventListener('animationend', onEnd);
      btn._pbEffectCleanup = function() {
        btn.removeEventListener('mouseenter', trigger);
        btn.removeEventListener('focus', trigger);
        btn.removeEventListener('animationend', onEnd);
      };
    })(btns[i]);
  }
})();`;

// ── Button Hover Effect: Rubber Band ──────────────────────────────────
const RUBBER_EFFECT_CSS = (SEL) => `
${SEL}::before { display: none; }
${SEL}:hover {
  animation: pbRubberBand 0.6s ease;
}
@keyframes pbRubberBand {
  0% { transform: scale(1); }
  30% { transform: scale(1.15, 0.85); }
  50% { transform: scale(0.95, 1.05); }
  70% { transform: scale(1.05, 0.95); }
  100% { transform: scale(1); }
}`;

// ── Button Hover Effect: Neon Glow ────────────────────────────────────
const GLOW_EFFECT_CSS = (SEL) => `
${SEL}::before { display: none; }
${SEL} { transition: box-shadow 0.3s, transform 0.3s, color 0.3s; }
${SEL}:hover {
  box-shadow: 0 0 10px var(--primary), 0 0 30px var(--primary), 0 0 60px var(--primary);
  transform: scale(1.03);
}`;

// ── Button Hover Effect: Magnetic Pull ────────────────────────────────
const MAGNETIC_EFFECT_CSS = (SEL) => `
${SEL}::before { display: none; }
${SEL} { transition: transform 0.2s ease, color 0.3s; }
`;

const MAGNETIC_EFFECT_JS = (SEL) =>
  EFFECT_CLEANUP(SEL) +
  `
(function() {
  var btns = document.querySelectorAll('${SEL}');
  for (var i = 0; i < btns.length; i++) {
    (function(btn) {
      function onMove(e) {
        var rect = btn.getBoundingClientRect();
        var x = (e.clientX - rect.left - rect.width / 2) * 0.15;
        var y = (e.clientY - rect.top - rect.height / 2) * 0.15;
        btn.style.transform = 'translate(' + x + 'px, ' + y + 'px)';
      }
      function onLeave() { btn.style.transform = ''; }
      btn.addEventListener('mousemove', onMove);
      btn.addEventListener('mouseleave', onLeave);
      btn._pbEffectCleanup = function() {
        btn.removeEventListener('mousemove', onMove);
        btn.removeEventListener('mouseleave', onLeave);
        btn.style.transform = '';
      };
    })(btns[i]);
  }
})();`;

// ── Button Hover Effect: Wipe Fill ───────────────────────────────────
// Flat left-to-right color fill — classic wipe, no skew.
const WIPE_EFFECT_CSS = (SEL) => `
${SEL} { overflow: hidden; position: relative; isolation: isolate; z-index: 0; }
${SEL}::before { display: none !important; }
${SEL}::after {
  content: ""; position: absolute; display: block !important;
  top: 0; left: 0; width: 100%; height: 100%;
  background: var(--primary); z-index: -1;
  transform: scaleX(0); transform-origin: left center;
  transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  pointer-events: none; border-radius: inherit;
}
${SEL}:hover::after { transform: scaleX(1); }
${SEL}:hover { color: var(--bodyTextColorWhite, #fff); }`;

// ── Button Hover Effect: Ripple ──────────────────────────────────────
// Material-style expanding circle from click point.
const RIPPLE_EFFECT_CSS = (SEL) => `
${SEL} { overflow: hidden; position: relative; }
${SEL}::before { display: none; }
${SEL} .pb-ripple {
  position: absolute; border-radius: 50%;
  background: rgba(255,255,255,0.4);
  transform: scale(0); animation: pbRipple 0.6s ease-out;
  pointer-events: none;
}
@keyframes pbRipple {
  to { transform: scale(4); opacity: 0; }
}`;

const RIPPLE_EFFECT_JS = (SEL) =>
  EFFECT_CLEANUP(SEL) +
  `
(function() {
  var btns = document.querySelectorAll('${SEL}');
  for (var i = 0; i < btns.length; i++) {
    (function(btn) {
      function trigger(e) {
        var rect = btn.getBoundingClientRect();
        var size = Math.max(rect.width, rect.height);
        var el = document.createElement('span');
        el.className = 'pb-ripple';
        el.style.width = el.style.height = size + 'px';
        el.style.left = (e.clientX - rect.left - size / 2) + 'px';
        el.style.top = (e.clientY - rect.top - size / 2) + 'px';
        btn.appendChild(el);
        el.addEventListener('animationend', function() { el.remove(); });
      }
      btn.addEventListener('mouseenter', trigger);
      btn._pbEffectCleanup = function() {
        btn.removeEventListener('mouseenter', trigger);
        var old = btn.querySelectorAll('.pb-ripple');
        for (var r = 0; r < old.length; r++) old[r].remove();
      };
    })(btns[i]);
  }
})();`;

// ── Button Hover Effect: Glitch ──────────────────────────────────────
// Cyber-style offset glitch with color-split afterimages.
const GLITCH_EFFECT_CSS = (SEL) => `
${SEL} { position: relative; }
${SEL}::before, ${SEL}::after {
  content: attr(aria-label); position: absolute; display: none;
  top: 0; left: 0; right: 0; bottom: 0;
  display: flex; align-items: center; justify-content: center;
  overflow: hidden; pointer-events: none; border-radius: inherit;
  background: inherit; color: inherit; font: inherit;
}
${SEL}::before { display: none !important; }
${SEL}::after { display: none !important; }
${SEL}.pb-animate::before {
  display: flex !important;
  clip-path: inset(0 0 50% 0);
  animation: pbGlitchTop 0.4s steps(2, jump-none);
  color: cyan;
  mix-blend-mode: screen;
  opacity: 0.8;
}
${SEL}.pb-animate::after {
  display: flex !important;
  clip-path: inset(50% 0 0 0);
  animation: pbGlitchBottom 0.4s steps(2, jump-none);
  color: magenta;
  mix-blend-mode: screen;
  opacity: 0.8;
}
@keyframes pbGlitchTop {
  0% { transform: translate(0); }
  25% { transform: translate(-4px, -2px); }
  50% { transform: translate(4px, 1px); }
  75% { transform: translate(-2px, 2px); }
  100% { transform: translate(0); }
}
@keyframes pbGlitchBottom {
  0% { transform: translate(0); }
  25% { transform: translate(3px, 2px); }
  50% { transform: translate(-3px, -1px); }
  75% { transform: translate(2px, -2px); }
  100% { transform: translate(0); }
}`;

const GLITCH_EFFECT_JS = (SEL) =>
  EFFECT_CLEANUP(SEL) +
  `
(function() {
  var btns = document.querySelectorAll('${SEL}');
  for (var i = 0; i < btns.length; i++) {
    (function(btn) {
      function trigger() {
        btn.classList.remove('pb-animate');
        void btn.offsetWidth;
        btn.classList.add('pb-animate');
      }
      function onEnd() { btn.classList.remove('pb-animate'); }
      btn.addEventListener('mouseenter', trigger);
      btn.addEventListener('animationend', onEnd);
      btn._pbEffectCleanup = function() {
        btn.removeEventListener('mouseenter', trigger);
        btn.removeEventListener('animationend', onEnd);
      };
    })(btns[i]);
  }
})();`;

// ── Button Hover Effect: 3D Tilt ─────────────────────────────────────
// Perspective tilt that tracks the cursor — gives buttons physical depth.
const TILT_EFFECT_CSS = (SEL) => `
${SEL}::before { display: none; }
${SEL} { transition: transform 0.15s ease, box-shadow 0.15s ease; transform-style: preserve-3d; }
${SEL}:hover { box-shadow: 0 8px 20px rgba(0,0,0,0.15); }`;

const TILT_EFFECT_JS = (SEL) =>
  EFFECT_CLEANUP(SEL) +
  `
(function() {
  var btns = document.querySelectorAll('${SEL}');
  for (var i = 0; i < btns.length; i++) {
    (function(btn) {
      function onMove(e) {
        var rect = btn.getBoundingClientRect();
        var x = (e.clientX - rect.left) / rect.width - 0.5;
        var y = (e.clientY - rect.top) / rect.height - 0.5;
        btn.style.transform = 'perspective(500px) rotateY(' + (x * 12) + 'deg) rotateX(' + (-y * 12) + 'deg) scale(1.03)';
      }
      function onLeave() { btn.style.transform = ''; }
      btn.addEventListener('mousemove', onMove);
      btn.addEventListener('mouseleave', onLeave);
      btn._pbEffectCleanup = function() {
        btn.removeEventListener('mousemove', onMove);
        btn.removeEventListener('mouseleave', onLeave);
        btn.style.transform = '';
      };
    })(btns[i]);
  }
})();`;

// ── Button Hover Effect: Spotlight ───────────────────────────────────
// Radial light disc follows the cursor across the button surface.
const SPOTLIGHT_EFFECT_CSS = (SEL) => `
${SEL} { overflow: hidden; position: relative; isolation: isolate; }
${SEL}::before { display: none !important; }
${SEL}::after {
  content: ""; position: absolute; display: block !important;
  width: 200%; height: 200%; top: -50%; left: -50%;
  background: radial-gradient(circle 60px at var(--pb-spot-x, 50%) var(--pb-spot-y, 50%), rgba(255,255,255,0.35) 0%, transparent 100%);
  pointer-events: none; z-index: 2; opacity: 0;
  transition: opacity 0.25s;
  border-radius: inherit;
}
${SEL}:hover::after { opacity: 1; }`;

const SPOTLIGHT_EFFECT_JS = (SEL) =>
  EFFECT_CLEANUP(SEL) +
  `
(function() {
  var btns = document.querySelectorAll('${SEL}');
  for (var i = 0; i < btns.length; i++) {
    (function(btn) {
      function onMove(e) {
        var rect = btn.getBoundingClientRect();
        var x = ((e.clientX - rect.left) / rect.width * 100).toFixed(1);
        var y = ((e.clientY - rect.top) / rect.height * 100).toFixed(1);
        btn.style.setProperty('--pb-spot-x', x + '%');
        btn.style.setProperty('--pb-spot-y', y + '%');
      }
      function onLeave() {
        btn.style.removeProperty('--pb-spot-x');
        btn.style.removeProperty('--pb-spot-y');
      }
      btn.addEventListener('mousemove', onMove);
      btn.addEventListener('mouseleave', onLeave);
      btn._pbEffectCleanup = function() {
        btn.removeEventListener('mousemove', onMove);
        btn.removeEventListener('mouseleave', onLeave);
        onLeave();
      };
    })(btns[i]);
  }
})();`;

// ── Button Effect Registry ────────────────────────────────────────────

const EFFECT_BUILDERS = {
  none: { css: null, js: null },
  bubble: { css: BUBBLE_EFFECT_CSS, js: BUBBLE_EFFECT_JS },
  shine: { css: SHINE_EFFECT_CSS, js: SHINE_EFFECT_JS },
  rubber: { css: RUBBER_EFFECT_CSS, js: null },
  glow: { css: GLOW_EFFECT_CSS, js: null },
  magnetic: { css: MAGNETIC_EFFECT_CSS, js: MAGNETIC_EFFECT_JS },
  wipe: { css: WIPE_EFFECT_CSS, js: null },
  ripple: { css: RIPPLE_EFFECT_CSS, js: RIPPLE_EFFECT_JS },
  glitch: { css: GLITCH_EFFECT_CSS, js: GLITCH_EFFECT_JS },
  tilt: { css: TILT_EFFECT_CSS, js: TILT_EFFECT_JS },
  spotlight: { css: SPOTLIGHT_EFFECT_CSS, js: SPOTLIGHT_EFFECT_JS }
};

/** Effect names, independent of any selector. */
export const VALID_EFFECTS = Object.keys(EFFECT_BUILDERS);

// One registry per distinct selector. A project's selector is stable across
// renders, so this is a two-entry cache in practice (default + the project's).
const registryCache = new Map();

/**
 * Build the `{effect: {css, js}}` registry for a given button selector.
 * Memoized — eleven template literals per render is not free.
 */
export function buildButtonEffectRegistry(selector) {
  const sel = selector || DEFAULT_BUTTON_SELECTOR;
  const cached = registryCache.get(sel);
  if (cached) return cached;
  const registry = {};
  for (const [name, builder] of Object.entries(EFFECT_BUILDERS)) {
    registry[name] = {
      css: builder.css ? builder.css(sel) : '',
      js: builder.js ? builder.js(sel) : ''
    };
  }
  registryCache.set(sel, registry);
  return registry;
}

/** The default-selector registry, for callers with no project in scope. */
export const BUTTON_EFFECT_REGISTRY = buildButtonEffectRegistry(DEFAULT_BUTTON_SELECTOR);

export function getButtonEffectJS(state, selector) {
  const effect = state?.buttonStyles?.hoverEffect || 'none';
  return buildButtonEffectRegistry(selector)[effect]?.js || '';
}
